import { Card, Text } from '@mantine/core'
import { useEffect, useRef } from 'react'
import mermaid from 'mermaid'
import type { ServiceDependency } from '@/types'

interface ServiceDependencyGraphProps {
  dependencies: ServiceDependency[]
}

export function ServiceDependencyGraph({ dependencies }: ServiceDependencyGraphProps) {
  const graphRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!dependencies.length || !graphRef.current) return

    // Generate mermaid graph definition
    const nodes = new Set<string>()
    dependencies.forEach((dep) => {
      nodes.add(dep.from_service)
      nodes.add(dep.to_service)
    })

    let graphDef = 'graph LR\n'
    
    // Add nodes
    nodes.forEach((node) => {
      graphDef += `    ${node}[${node}]\n`
    })
    
    // Add edges
    dependencies.forEach((dep) => {
      const label = `${dep.call_count} calls\\n${dep.avg_duration_ms.toFixed(0)}ms`
      const style = dep.error_count > 0 ? 'stroke:#ff0000' : ''
      graphDef += `    ${dep.from_service} -->|${label}| ${dep.to_service}\n`
      if (style) {
        graphDef += `    linkStyle ${dependencies.indexOf(dep)} ${style}\n`
      }
    })

    // Initialize mermaid
    mermaid.initialize({
      startOnLoad: true,
      theme: 'default',
      flowchart: {
        useMaxWidth: true,
        htmlLabels: true,
      },
    })

    // Render graph
    graphRef.current.innerHTML = `<div class="mermaid">${graphDef}</div>`
    mermaid.init(undefined, graphRef.current)
  }, [dependencies])

  return (
    <Card shadow="sm" p="lg" radius="md">
      <Text size="lg" fw={600} mb="md">
        Service Dependencies
      </Text>
      <div ref={graphRef} style={{ minHeight: 400 }} />
    </Card>
  )
}